import { FolderIcon, PlusIcon } from "@heroicons/react/24/outline";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-hot-toast";
import { Link, useNavigate } from "react-router-dom";
import Button from "../common/Button";
import { SortState } from "../common/ListViewControls";
import ListPage, { ListColumn } from "../common/ListPage";
import UploadModal from "../Menu/UploadModal";
import { FILES_CHANGED_EVENT, isUploadActive, saveFilesToLibrary } from "../../services/libraryUploadService";
import Dropdown from "../Settings/Dropdown";
import { AudioType, fileConfigFromLibraryItem, FileKind, FileSource } from "../Menu/uploadFileConfig";
import { openReportUpload } from "../Menu/uploadLauncher";
import { FILE_KIND_LABELS, FILE_SOURCE_LABELS, filesApi, FilesItem } from "./api";
import { AudioTypeBadge, KindIcon, shortDate, StatusChip, TranscriptChip } from "./fileKinds";

// A report takes at most this many files (the upload modal's limit).
const MAX_SELECTED = 10;
const PAGE_SIZE = 25;

type SortKey = "created_at" | "name" | "kind" | "size";

const keyOf = (f: FilesItem) => `${f.source}:${f.id}`;

// Picker mode: the same list, used inside a modal to choose files for something
// else (the Fill Form picker). Nothing in it leaves the page: rows open through
// `onOpenFile`, and the action bar offers the host's action instead of Create
// report / Delete.
export interface FilesPickerOptions {
  confirmLabel: (count: number) => string;
  onConfirm: (items: FilesItem[]) => void;
  onOpenFile: (item: FilesItem) => void;
}

// Why a file cannot be ticked when it is going to be READ (not just listed).
export const unreadableReason = (f: FilesItem): string | undefined => {
  if (f.status !== "completed") return "Available once processing finishes";
  if ((f.kind === "audio" || f.kind === "video") && !f.metadata?.transcriptionUrl) {
    return "No transcript yet. Open it to transcribe it.";
  }
  return undefined;
};

const FilesList: React.FC<{ picker?: FilesPickerOptions }> = ({ picker }) => {
  const navigate = useNavigate();
  const [files, setFiles] = useState<FilesItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [kind, setKind] = useState<FileKind | "">("");
  const [source, setSource] = useState<FileSource | "">("");
  const [audioType, setAudioType] = useState<AudioType | "other" | "">("");
  const [sort, setSort] = useState<SortState<SortKey>>({ key: "created_at", dir: "desc" });
  // Selection survives paging and filtering, so a report can mix files found
  // under different searches.
  const [selected, setSelected] = useState<Map<string, FilesItem>>(new Map());
  // Delete: the files being deleted, and for each the reports that use it.
  const [pendingDelete, setPendingDelete] = useState<FilesItem[]>([]);
  const [usage, setUsage] = useState<Record<string, { caseId: string; caseNumber: string }[]> | null>(null);
  const [deleting, setDeleting] = useState(false);
  const deleteDialogRef = useRef<HTMLDialogElement>(null);
  const addModalRef = useRef<HTMLDialogElement>(null);
  // Files dropped on the page; the Add files modal opens with them loaded.
  const [droppedFiles, setDroppedFiles] = useState<File[] | undefined>();

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setPage(0);
  }, [debouncedSearch, kind, source, audioType, sort]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await filesApi.list({
        search: debouncedSearch || undefined,
        kind,
        source,
        audioType,
        sort: sort.key,
        dir: sort.dir,
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
      });
      // A library row still "waiting" for its bytes that this tab is not
      // uploading was interrupted (refresh, closed tab): it will never finish.
      setFiles(
        res.files.map((f) =>
          f.source === "library" && !f.fileUrl && (f.status === "pending" || f.status === "uploading") && !isUploadActive(f.id)
            ? { ...f, status: "failed" as const, error: "Upload interrupted. Delete this and add the file again." }
            : f,
        ),
      );
      setTotal(res.meta.count);
    } catch (error) {
      console.error("Error loading files:", error);
      toast.error("Failed to load files");
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, kind, source, audioType, sort, page]);

  useEffect(() => {
    load();
  }, [load]);

  // Uploads finish in the background; refresh the list when one lands.
  useEffect(() => {
    window.addEventListener(FILES_CHANGED_EVENT, load);
    return () => window.removeEventListener(FILES_CHANGED_EVENT, load);
  }, [load]);

  // Library rows that are still being transcribed or extracted: poll until done.
  const hasInFlight = files.some((f) => ["pending", "uploading", "processing"].includes(f.status));
  useEffect(() => {
    if (!hasInFlight) return;
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [hasInFlight, load]);

  const toggle = (f: FilesItem) => {
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(keyOf(f))) next.delete(keyOf(f));
      else if (next.size < MAX_SELECTED) next.set(keyOf(f), f);
      return next;
    });
  };

  const atLimit = selected.size >= MAX_SELECTED;
  const selectedList = Array.from(selected.values());

  const createReport = () => {
    openReportUpload(selectedList.map(fileConfigFromLibraryItem));
  };

  // Open the dialog right away, then look up which reports use each file so
  // the warning can name them.
  const askDelete = (items: FilesItem[]) => {
    setPendingDelete(items);
    setUsage(null);
    deleteDialogRef.current?.showModal();
    Promise.all(
      items.map((f) =>
        filesApi
          .get(f.source, f.id)
          .then((d) => [keyOf(f), d.usedIn] as const)
          .catch(() => [keyOf(f), []] as const),
      ),
    ).then((pairs) => setUsage(Object.fromEntries(pairs)));
  };

  const confirmDelete = async () => {
    setDeleting(true);
    let failed = 0;
    for (const f of pendingDelete) {
      try {
        await filesApi.remove(f.source, f.id);
        setSelected((prev) => {
          const next = new Map(prev);
          next.delete(keyOf(f));
          return next;
        });
      } catch (error) {
        failed++;
        console.error("Error deleting file:", error);
      }
    }
    setDeleting(false);
    deleteDialogRef.current?.close();
    if (failed) toast.error(failed === pendingDelete.length ? "Failed to delete" : `${failed} could not be deleted`);
    else toast.success(pendingDelete.length === 1 ? "File deleted" : `${pendingDelete.length} files deleted`);
    load();
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const filtered = Boolean(debouncedSearch || kind || source || audioType);

  const columns: ListColumn<FilesItem, SortKey>[] = [
    {
      key: "name",
      header: "Name",
      sortKey: "name",
      width: 380,
      minWidth: 180,
      className: "pl-1 overflow-hidden",
      headerClassName: "pl-1",
      render: (f) => (
        <div className="flex items-center gap-2 min-w-0">
          <KindIcon kind={f.kind} className="shrink-0 text-base-content/35" />
          <span className="font-medium text-base-content truncate">{f.fileName || "Untitled"}</span>
          <StatusChip status={f.status} error={f.error} />
        </div>
      ),
    },
    {
      key: "kind",
      header: "Type",
      sortKey: "kind",
      width: 120,
      minWidth: 80,
      hideBelow: "md",
      truncate: true,
      className: "text-sm text-base-content/60",
      render: (f) => FILE_KIND_LABELS[f.kind],
    },
    {
      key: "audio",
      header: "Audio type",
      width: 130,
      minWidth: 100,
      hideBelow: "md",
      render: (f) =>
        (f.kind === "audio" || f.kind === "video") && (f.metadata.audioType || f.metadata.isOther) ? (
          <AudioTypeBadge type={f.metadata.audioType || "other"} />
        ) : (
          <span className="text-base-content/40">{"\u2014"}</span>
        ),
    },
    {
      key: "transcript",
      header: "Transcript",
      width: 150,
      minWidth: 120,
      hideBelow: "md",
      render: (f) => <TranscriptChip file={f} />,
    },
    {
      key: "from",
      header: "From",
      width: 240,
      minWidth: 110,
      hideBelow: "lg",
      truncate: true,
      className: "text-sm text-base-content/60",
      render: (f) => (f.parentLabel ? `${FILE_SOURCE_LABELS[f.source]} \u00b7 ${f.parentLabel}` : FILE_SOURCE_LABELS[f.source]),
    },
    {
      key: "size",
      header: "Size",
      sortKey: "size",
      defaultDir: "desc",
      width: 100,
      minWidth: 70,
      hideBelow: "md",
      truncate: true,
      className: "text-sm text-base-content/60 tabular-nums",
      render: (f) => f.fileSize || "\u2014",
    },
    {
      key: "added",
      header: "Added",
      sortKey: "created_at",
      defaultDir: "desc",
      width: 140,
      minWidth: 100,
      truncate: true,
      className: "text-sm text-base-content/60 tabular-nums",
      render: (f) => shortDate(f.createdAt),
    },
    {
      // No width: takes the leftover space, so the other columns keep exact widths.
      key: "actions",
      header: "",
      minWidth: 90,
      align: "right",
      render: (f) =>
        f.source === "library" && f.status === "failed" && f.fileUrl ? (
          <Button
            variant="secondary"
            size="xs"
            onClick={async () => {
              try {
                await filesApi.retry(f.id);
                load();
              } catch {
                toast.error("Could not retry. Please try again.");
              }
            }}
          >
            Retry
          </Button>
        ) : null,
    },
  ];

  return (
    <ListPage<FilesItem, SortKey>
      title={picker ? "Choose files" : "Files"}
      subtitle={
        picker
          ? "Tick the files to read, or click one to look at it first."
          : "Interviews, recordings and documents, saved here or uploaded to reports. Tick files to start a report from them."
      }
      // One primary per screen: Add files steps back once a selection offers Create report.
      action={
        picker ? undefined : (
          <Button variant={selected.size > 0 ? "secondary" : "primary"} onClick={() => addModalRef.current?.showModal()}>
            <PlusIcon className="h-5 w-5 mr-1" />
            Add files
          </Button>
        )
      }
      items={files}
      getKey={keyOf}
      columns={columns}
      sort={sort}
      onSort={setSort}
      onRowClick={(f) => (picker ? picker.onOpenFile(f) : navigate(`/files/${f.source}/${f.id}`))}
      resizeStorageKey="pnai-files-col-widths"
      onFilesDropped={
        picker
          ? undefined
          : (dropped) => {
              setDroppedFiles(dropped);
              addModalRef.current?.showModal();
            }
      }
      dropMessage="Drop files to add them to Files"
      search={{ value: search, onChange: setSearch, placeholder: "Search files..." }}
      filters={
        <>
          <Dropdown
            size="sm"
            dense
            value={kind}
            onChange={(v) => setKind(v as FileKind | "")}
            options={[
              { value: "", label: "All types" },
              ...(Object.keys(FILE_KIND_LABELS) as FileKind[]).map((k) => ({ value: k, label: FILE_KIND_LABELS[k] })),
            ]}
          />
          <Dropdown
            size="sm"
            dense
            value={audioType}
            onChange={(v) => setAudioType(v as AudioType | "other" | "")}
            options={[
              { value: "", label: "All audio types" },
              { value: "narrative", label: "Narrative" },
              { value: "interaction", label: "Interaction" },
              { value: "other", label: "Other" },
            ]}
          />
          <Dropdown
            size="sm"
            dense
            value={source}
            onChange={(v) => setSource(v as FileSource | "")}
            options={[
              { value: "", label: "All sources" },
              ...(Object.keys(FILE_SOURCE_LABELS) as FileSource[]).map((s) => ({ value: s, label: FILE_SOURCE_LABELS[s] })),
            ]}
          />
        </>
      }
      selection={{
        selected: new Set(selected.keys()),
        onToggle: toggle,
        noun: "file",
        note: atLimit ? `Up to ${MAX_SELECTED} files${picker ? "" : " per report"}` : undefined,
        // Files still being processed can't be selected. Failed ones can (to
        // delete them), but Create report needs every selected file finished.
        disabledReason: (f) =>
          picker && unreadableReason(f)
            ? unreadableReason(f)
            : f.status !== "completed" && f.status !== "failed"
              ? "Available once processing finishes"
              : !selected.has(keyOf(f)) && atLimit
                ? `Up to ${MAX_SELECTED} files${picker ? "" : " per report"}`
                : undefined,
        actions: picker ? (
          <>
            <Button variant="primary" size="sm" onClick={() => picker.onConfirm(selectedList)}>
              {picker.confirmLabel(selectedList.length)}
            </Button>
            <Button variant="clean" size="sm" onClick={() => setSelected(new Map())}>
              Clear
            </Button>
          </>
        ) : (
          <>
            <Button
              variant="primary"
              size="sm"
              onClick={createReport}
              disabled={selectedList.some((f) => f.status !== "completed")}
              title={selectedList.some((f) => f.status !== "completed") ? "Every selected file must be finished" : undefined}
            >
              Create report
            </Button>
            <Button variant="danger" size="sm" onClick={() => askDelete(selectedList)}>
              Delete
            </Button>
            <Button variant="clean" size="sm" onClick={() => setSelected(new Map())}>
              Clear
            </Button>
          </>
        ),
      }}
      // Keep the rows on screen while a poll or filter change refetches.
      loading={loading && files.length === 0}
      isFiltered={filtered}
      noMatch="No files match your search."
      empty={{
        icon: <FolderIcon className="h-12 w-12" />,
        title: "No files yet",
        description: picker ? "Nothing has been saved to Files yet." : "Audio and documents you upload to reports show up here.",
        action: picker ? undefined : (
          <Button variant="primary" onClick={() => addModalRef.current?.showModal()}>
            <PlusIcon className="h-5 w-5 mr-1" />
            Add files
          </Button>
        ),
      }}
      footer={
        total > PAGE_SIZE && (
          <div className="flex items-center justify-between text-sm text-base-content/70">
            <span>
              Page {page + 1} of {totalPages} · {total} files
            </span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
                Previous
              </Button>
              <Button variant="outline" size="sm" disabled={page + 1 >= totalPages} onClick={() => setPage((p) => p + 1)}>
                Next
              </Button>
            </div>
          </div>
        )
      }
    >
      {!picker && (
        <>
      <UploadModal
        modalRef={addModalRef as React.RefObject<HTMLDialogElement>}
        mode="library"
        allowDocuments
        droppedFiles={droppedFiles}
        onUpload={(configs) => {
          saveFilesToLibrary(configs);
        }}
      />

      <dialog ref={deleteDialogRef} className="modal" onClose={() => setPendingDelete([])}>
        <div className="modal-box bg-base-100 max-w-lg">
          <h3 className="font-semibold text-lg text-base-content">
            Delete {pendingDelete.length === 1 ? "this file" : `${pendingDelete.length} files`}?
          </h3>

          <ul className="mt-3 flex flex-col gap-3 max-h-[50vh] overflow-y-auto">
            {pendingDelete.map((f) => {
              const reports = usage?.[keyOf(f)] ?? [];
              return (
                <li key={keyOf(f)} className="text-sm">
                  <div className="flex items-center gap-2">
                    <KindIcon kind={f.kind} className="shrink-0" />
                    <span className="font-medium text-base-content break-all">{f.fileName || "Untitled"}</span>
                  </div>
                  {usage === null ? (
                    <div className="mt-1 ml-7 text-xs text-base-content/50">Checking which reports use it...</div>
                  ) : reports.length > 0 ? (
                    <div className="mt-2 ml-7 rounded-lg border border-amber-300 dark:border-amber-700/60 bg-amber-50 dark:bg-amber-950/40 px-3 py-2 text-amber-900 dark:text-amber-200">
                      <div className="font-semibold">
                        Used in {reports.length} report{reports.length === 1 ? "" : "s"}
                      </div>
                      <div className="mt-1 flex flex-wrap gap-1.5">
                        {reports.map((r) => (
                          <Link
                            key={r.caseId}
                            to={`/cases/${r.caseId}`}
                            onClick={() => deleteDialogRef.current?.close()}
                            className="px-2 py-0.5 rounded-full bg-base-100/70 dark:bg-base-100/10 text-xs font-semibold underline-offset-2 hover:underline"
                          >
                            {r.caseNumber || "Report"}
                          </Link>
                        ))}
                      </div>
                      <p className="mt-1.5 text-xs">
                        {f.source === "library"
                          ? "Those reports keep their own copy, but the file will no longer be in Files."
                          : "The reports keep the file. It will only be removed from Files."}
                      </p>
                    </div>
                  ) : (
                    <div className="mt-1 ml-7 text-xs text-base-content/50">Not used in any report.</div>
                  )}
                </li>
              );
            })}
          </ul>

          <div className="modal-action">
            <Button variant="outline" size="sm" onClick={() => deleteDialogRef.current?.close()} disabled={deleting}>
              Cancel
            </Button>
            <Button variant="danger" size="sm" onClick={confirmDelete} loading={deleting} disabled={usage === null}>
              {usage && Object.values(usage).some((r) => r.length > 0) ? "Delete anyway" : "Delete"}
            </Button>
          </div>
        </div>
        <form method="dialog" className="modal-backdrop">
          <button>close</button>
        </form>
      </dialog>
        </>
      )}
    </ListPage>
  );
};

export default FilesList;
